import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { Label } from "@/modern/components/ui/label";
import { Input } from "@/modern/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/modern/components/ui/select";
import { Card } from "@/modern/components/ui/card";
import { Button } from "@/modern/components/ui/button";
import { Download, Info } from "lucide-react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { GRANTS } from "@/other/constants/PrivilegeTypes";
import { exportBoardApi } from "@/network/network";
import { isIAmOneOfBoardOwner, downloadFileFromData, showToast } from "@/other/utils/utils";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { User } from "@/bryntum-lib/models/User";
import { toast } from "sonner";
import { debounce } from "@/other/utils/otherUtils";
import { ASYNC_TASK_TYPES } from "@/other/constants/OtherKeys";
import { AsyncTaskUI, useAsyncTaskUIContext } from "@/modern/components/async-tasks/async-task-ui";
import { useAsyncTaskContext } from "@/modern/components/async-tasks/context/AsyncTaskContext";
import type { AsyncTask } from "@/modern/components/async-tasks/types";
import {
  extractAsyncTaskId,
  getTaskStatusMessage,
  isAsyncTaskType,
  matchBoardExportTask,
} from "@/modern/components/async-tasks/utils";
import { downloadBoardExportResult } from "@/modern/components/async-tasks/utils/boardExportDownload";

interface GeneralBoardSettingDetailBodyProps {
  subcategory: SubcategoryDetailProps["subcategory"];
  boardName: string;
  boardOwner: number | null;
  boardOwners: User[];
  isDisabled: boolean;
  canEdit: boolean;
  canShareOrExport: boolean;
  isSubmittingExport: boolean;
  onBoardNameChange: (value: string) => void;
  onBoardOwnerChange: (value: string) => void;
  onExportBoard: () => void;
}

function GeneralBoardSettingDetailBody({
  subcategory,
  boardName,
  boardOwner,
  boardOwners,
  isDisabled,
  canEdit,
  canShareOrExport,
  isSubmittingExport,
  onBoardNameChange,
  onBoardOwnerChange,
  onExportBoard,
}: GeneralBoardSettingDetailBodyProps) {
  const { hasPending } = useAsyncTaskUIContext();
  const isExporting = isSubmittingExport || hasPending;

  return (
    <div className="p-6">
      <AsyncTaskUI.Banner className="mb-4" />
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">{subcategory.description}</p>
      </div>

      <div className="space-y-5 max-w-3xl">
        <Card className="py-3 px-4">
          <div className="space-y-6">
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">Board Information</h3>
              <p className="text-xs text-muted-foreground">Basic board settings and ownership</p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="board-name">Board Name</Label>
                <Input
                  variant="background"
                  inputSize="selectTrigger"
                  id="board-name"
                  type="text"
                  value={boardName}
                  onChange={(e) => onBoardNameChange(e.target.value)}
                  disabled={isDisabled}
                  placeholder="Enter board name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="board-owner">Board Owner</Label>
                <Select
                  value={boardOwner?.toString() || ""}
                  onValueChange={onBoardOwnerChange}
                  disabled={!canEdit}
                >
                  <SelectTrigger id="board-owner">
                    <SelectValue placeholder="Select board owner" />
                  </SelectTrigger>
                  <SelectContent>
                    {boardOwners.map((owner) => (
                      <SelectItem key={owner.id} value={owner.id.toString()}>
                        {owner.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Card className="py-3 px-4 border-primary/20 bg-primary/5">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <Info className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-foreground leading-relaxed">
                      The Board Owner cannot be deleted. However, you can designate another Administrator as the Board Owner.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </Card>

        <Card className="py-3 px-4">
          <div className="space-y-6">
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">Board Actions</h3>
              <p className="text-xs text-muted-foreground">Export the entire board data</p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                onClick={onExportBoard}
                disabled={!canShareOrExport || isExporting}
                className="flex items-center gap-2"
                variant="outline"
              >
                <Download className="h-4 w-4" />
                {isExporting ? "Exporting..." : "Export Board"}
              </Button>
            </div>

            <Card className="py-3 px-4 border-primary/20 bg-primary/5">
              <div className="flex gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <Info className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-foreground leading-relaxed">
                    Looking to share your schedule? Visit <strong>Schedule → Share Schedule</strong> in the settings menu.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </Card>
      </div>
    </div>
  );
}

const GeneralBoardSettingDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { project, user, boardManager, selectedBoard } = useSwivuBryntumContext();
  const [boardName, setBoardName] = useState("");
  const [boardOwner, setBoardOwner] = useState<number | null>(null);
  const [boardOwners, setBoardOwners] = useState<User[]>([]);
  const [isSubmittingExport, setIsSubmittingExport] = useState(false);
  const [canEdit, setCanEdit] = useState(false);

  const { pinTask } = useAsyncTaskContext();
  const pendingExportFileNameRef = useRef("board-export.json");

  const boardId = selectedBoard?.id as number | undefined;

  const taskFilter = useCallback(
    (task: AsyncTask) =>
      boardId != null &&
      matchBoardExportTask(
        task,
        boardId,
        boardName || selectedBoard?.name,
      ),
    [boardId, boardName, selectedBoard?.name],
  );

  const downloadBoardExportPayload = useCallback((data: unknown, fileName: string) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    downloadFileFromData(blob, fileName);
  }, []);

  const asyncEffects = useMemo(
    () => ({
      onCompleted: (task: AsyncTask) => {
        if (!isAsyncTaskType(task, ASYNC_TASK_TYPES.BOARD_EXPORT)) return;

        void downloadBoardExportResult(task.id, pendingExportFileNameRef.current)
          .then(() => {
            showToast({
              msg: getTaskStatusMessage(task),
              type: "success",
              duration: 3000,
            });
          })
          .catch(() => {
            showToast({
              msg: getTaskStatusMessage(task),
              type: "error",
              duration: 3000,
            });
          });
      },
      onFailed: (task: AsyncTask) => {
        if (!isAsyncTaskType(task, ASYNC_TASK_TYPES.BOARD_EXPORT)) return;
        showToast({
          msg: getTaskStatusMessage(task),
          type: "error",
          duration: 3000,
        });
      },
    }),
    [],
  );

  useSwivuStore({
    storeId: STORE_KEYS.boardUserStore,
    subscriberFn: () => {
      loadBoardData();
      return false;
    },
  });

  useEffect(() => {
    loadBoardData();
  }, [selectedBoard, project, user]);

  const loadBoardData = () => {
    if (!selectedBoard || !project || !user || !boardManager) return;

    setBoardName(selectedBoard.name || "");

    const administrators =
      project.boardUsersStore.query(
        (boardUser: User) => boardUser.userId && boardUser.roles === GRANTS.grant_one,
        true,
      ) || [];
    setBoardOwners(administrators);

    const boardStore = boardManager.boardNameStore?.getById(selectedBoard.id);
    if (boardStore) {
      // @ts-expect-error - boardStore.owner is dynamically typed
      setBoardOwner(boardStore.owner);

      // @ts-expect-error - boardStore.owner is dynamically typed
      const isOwner = isIAmOneOfBoardOwner(project, boardStore.owner);
      setCanEdit(isOwner && !user.isDemoUser && user.can(GRANTS.grant_one));
    }
  };

  const debouncedUpdateBoardName = useCallback(
    debounce((value: string) => {
      if (selectedBoard?.record) {
        selectedBoard.record.name = value;
      }
    }, 500),
    [selectedBoard],
  );

  const handleBoardNameChange = (value: string) => {
    setBoardName(value);
    debouncedUpdateBoardName(value);
  };

  const handleBoardOwnerChange = (value: string) => {
    const ownerId = parseInt(value, 10);
    setBoardOwner(ownerId);

    if (selectedBoard?.record && project && boardManager && user) {
      // @ts-expect-error - selectedBoard.record is dynamically typed
      selectedBoard.record.owner = ownerId;

      const isOwner = isIAmOneOfBoardOwner(project, ownerId);
      setCanEdit(isOwner && !user.isDemoUser && user.can(GRANTS.grant_one));
    }
  };

  const handleExportBoard = async () => {
    if (!selectedBoard || boardId == null) return;

    const exportFileName = `${(boardName || selectedBoard.name || "board").trim()}-export.json`;
    pendingExportFileNameRef.current = exportFileName;

    setIsSubmittingExport(true);
    try {
      const response = await exportBoardApi(selectedBoard.id);
      const taskId = extractAsyncTaskId(response.data);

      if (taskId != null) {
        pinTask(taskId, {
          type: ASYNC_TASK_TYPES.BOARD_EXPORT,
          meta_data: {
            board_id: boardId,
            board_name: boardName || selectedBoard.name || "board",
          },
        });
        return;
      }

      if (response.data != null) {
        downloadBoardExportPayload(response.data, exportFileName);
        showToast({
          msg: `"${boardName || selectedBoard.name}" export ready`,
          type: "success",
          duration: 3000,
        });
      }
    } catch {
      toast.error("Failed to export board. Something went wrong.");
    } finally {
      setIsSubmittingExport(false);
    }
  };

  const isDisabled = Boolean(user?.isDemoUser || !user?.can(GRANTS.grant_one));
  const canShareOrExport = Boolean(!user?.isDemoUser && user?.can(GRANTS.grant_one));

  if (boardId == null) {
    return null;
  }

  return (
    <AsyncTaskUI filter={taskFilter} effects={asyncEffects}>
      <GeneralBoardSettingDetailBody
        subcategory={subcategory}
        boardName={boardName}
        boardOwner={boardOwner}
        boardOwners={boardOwners}
        isDisabled={isDisabled}
        canEdit={canEdit}
        canShareOrExport={canShareOrExport}
        isSubmittingExport={isSubmittingExport}
        onBoardNameChange={handleBoardNameChange}
        onBoardOwnerChange={handleBoardOwnerChange}
        onExportBoard={handleExportBoard}
      />
    </AsyncTaskUI>
  );
};

export default GeneralBoardSettingDetail;
